import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { BasketService } from 'src/app/services/basket.service';
import { OrderService } from 'src/app/services/order.service';
import { WaiterCallReason, WaiterCallService } from 'src/app/services/waiter-call.service';

@Component({
    selector: 'app-waiter-call',
    templateUrl: './menu-waiter-call.component.html',
    styleUrls: ['./menu-waiter-call.component.css'],
    animations: [
        trigger('textFade', [
            state('visible', style({ opacity: 1, transform: 'translateY(0)' })),
            state('hidden', style({ opacity: 0, transform: 'translateY(10px)' })),
            transition('visible <=> hidden', animate('300ms ease-in-out'))
        ])
    ],
    standalone: false
})
export class MenuWaiterCallComponent implements OnInit, OnDestroy {
  constructor(
    public waiterCallService: WaiterCallService,
    private basketService: BasketService,
    private orderService: OrderService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
  ) {}

  /** Внизу корзина или панель заказа — статус вызова поднимается над ней */
  bottomBar = false;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    combineLatest([
      this.basketService.foods,
      this.basketService.drinks,
      // Общий заказ стола (его подписку ведёт меню), без своей подписки
      this.orderService.order$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([foods, drinks, order]) => {
        this.hasOrder = !!order?.guests;
        this.bottomBar = foods.length > 0 || drinks.length > 0 || this.hasOrder;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Причины в листе — только при активном заказе: счёт без заказа не нужен.
   * Без заказа свайп сразу зовёт официанта (причина «Позвать официанта»)
   */
  readonly reasons: { reason: WaiterCallReason; label: string; icon: 'bill' | 'help' }[] = [
    { reason: 'bill', label: 'callReasonBill', icon: 'bill' },
    { reason: 'help', label: 'callReasonHelp', icon: 'help' },
  ];

  /** Подпись причины вызова — в полосе статуса */
  reasonLabel(reason: WaiterCallReason | null): string {
    switch (reason) {
      case 'bill': return 'callReasonBill';
      case 'help': return 'callReasonHelp';
      default: return 'callReasonWaiter';
    }
  }

  /** У стола есть активный заказ */
  private hasOrder = false;

  /** Свайп: с заказом — лист причин, без заказа — сразу вызов */
  private onSwiped(): void {
    if (this.hasOrder) {
      this.waiterCallService.openSheet();
    } else {
      this.waiterCallService.call('waiter');
    }
  }

  /** «0:25», «2:45» */
  clock(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }

  /** Кружок, на который повешен свайп: полоса пересоздаётся при смене состояния */
  private boundCircle: HTMLElement | null = null;

  private isDragging = false;
  private startX: number = 0;
  private currentX: number = 0;

  @ViewChild('swipeCircle', { static: false }) swipeCircle!: ElementRef;
  @ViewChild('swipeContent', { static: false }) swipeContent!: ElementRef;

  ngAfterViewChecked() {
    const circle = this.swipeCircle?.nativeElement ?? null;
    if (circle && circle !== this.boundCircle) {
      this.boundCircle = circle;
      this.setupSwipe();
      this.cdr.detectChanges();
    }
  }

  private setupSwipe(): void {
    const circle = this.swipeCircle.nativeElement;
    const swipeContent = this.swipeContent.nativeElement;

    const onMove = (clientX: number) => {
      const maxX = swipeContent.offsetWidth - circle.offsetWidth;
      this.currentX = Math.max(0, Math.min(maxX, clientX - this.startX));
      this.renderer.setStyle(circle, 'transform', `translateX(${this.currentX}px)`);

      const swipeAreaWidth = swipeContent.offsetWidth - circle.offsetWidth;
      const progress = this.currentX / swipeAreaWidth;
      this.renderer.setStyle(swipeContent, 'opacity', `${1 - progress}`);
    };

    const onEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      const maxX = swipeContent.offsetWidth - circle.offsetWidth;
      this.renderer.setStyle(circle, 'transition', 'transform 0.5s ease-in-out');

      if (this.currentX < maxX * 0.9) {
        this.renderer.setStyle(circle, 'transform', 'translateX(0px)');
        this.renderer.setStyle(swipeContent, 'opacity', '1');
      } else {
        console.log('Swipe completed successfully!');
        this.renderer.setStyle(circle, 'transform', 'translateX(0px)');
        this.renderer.setStyle(swipeContent, 'opacity', '1');
        this.cdr.detectChanges();
        this.onSwiped();
      }

      this.currentX = 0;
      this.startX = 0;

    };

    // Обработчики событий
    this.renderer.listen(circle, 'touchstart', (event: TouchEvent) => {
      if (this.isDragging) event.preventDefault();
      event.preventDefault();
      this.isDragging = true;
      this.startX = event.touches[0].clientX;
      this.currentX = 0;
      // Отключаем transition во время перетаскивания для плавности
      this.renderer.setStyle(circle, 'transition', 'none');
    });

    this.renderer.listen(document, 'touchmove', (event: TouchEvent) => {
      if (!this.isDragging) return;
      onMove(event.touches[0].clientX);
    });

    this.renderer.listen(document, 'touchend', () => {
      onEnd();
    });

    // Mouse events
    this.renderer.listen(circle, 'mousedown', (event: MouseEvent) => {
      event.preventDefault();
      this.isDragging = true;
      this.startX = event.clientX;
      this.renderer.setStyle(circle, 'transition', 'none');
    });

    this.renderer.listen(document, 'mousemove', (event: MouseEvent) => {
      if (!this.isDragging) return;
      onMove(event.clientX);
    });

    this.renderer.listen(document, 'mouseup', () => {
      onEnd();
    });
  }
}
